/**
 * @file Contributor UI — mapApiStageToContributorSection.
 */

import { FORM_STAGE_CODES } from "../../../../constants/formStages";
import type { StageName } from "../types/contributorStageNames";

/**
 * Maps backend `submission.currentStage` to contributor sidebar / `activeSection` names.
 */
/** API stage strings → sidebar section (FORM_STAGE_CODES values are the duplicate-free keys). */
const EXACT: Record<string, StageName> = {
  [FORM_STAGE_CODES.CONTRIBUTOR_DETAILS]: "Contributor's Details",
  [FORM_STAGE_CODES.PUBLICATION_DETAILS]: "Publication Details",
  [FORM_STAGE_CODES.BACKGROUND_DATA]: "Background Data",
  [FORM_STAGE_CODES.A1_RAW_MATERIAL_SUPPLY]: "A1",
  [FORM_STAGE_CODES.A2_RAW_MATERIAL_TRANSPORT]: "A2",
  [FORM_STAGE_CODES.A3_RAW_MATERIAL_MANUFACTURING]: "A3",
};

/** Exported function — map Api Current Stage To Stage Name. */
export function mapApiCurrentStageToStageName(
  api?: string | null
): StageName | null {
  if (api == null) return null;
  const raw = String(api).trim();
  if (!raw) return null;

  const upper = raw.toUpperCase().replace(/\s+/g, "_");

  if (EXACT[raw]) return EXACT[raw];
  if (EXACT[upper]) return EXACT[upper];

  if (upper === "COMPLETED") return "Submitted Dataset";

  if (
    upper.includes("A1_A3") ||
    upper.includes("A1-A3") ||
    upper === "A1A3" ||
    (upper.includes("A1") && upper.includes("A3") && upper.includes("SUMMARY"))
  ) {
    return "A1-A3";
  }

  // Short codes from list endpoints or alternate backends (not only FORM_STAGE_CODES values)
  if (upper === "A1" || upper.endsWith("_A1")) return "A1";
  if (upper === "A2" || upper.endsWith("_A2")) return "A2";
  if (upper === "A3" || upper.endsWith("_A3")) return "A3";

  if (upper.includes("DATASET") && !upper.includes("SUBMITTED")) {
    return "Dataset";
  }
  if (upper.includes("SUBMITTED_DATASET") || upper === "SUBMITTED") {
    return "Submitted Dataset";
  }

  if (upper.includes("CONTRIBUTOR")) return "Contributor's Details";
  if (upper.includes("PUBLICATION")) return "Publication Details";
  if (upper.includes("BACKGROUND")) return "Background Data";

  if (upper.includes("TRANSPORT") && upper.includes("A2")) return "A2";
  if (upper.includes("MANUFACTURING") && upper.includes("A3")) return "A3";
  if (upper.includes("EXTRACTION") || upper.includes("RAW_MATERIAL_EXTRACTION"))
    return "A1";

  return null;
}
